/**
 * App Root Component
 * Assembles the complete provider tree in the correct nesting order:
 * Suspense → ErrorBoundary → I18n → QueryProvider → Theme → Session → Pendo → Router
 *
 * Provider tree order matters — providers higher in the tree are available
 * to all providers below. Session depends on Query, Router depends on Session, etc.
 */

import "./globals/initializeObservability"; // must be first import

import { GlobalStyles, ThemeProvider } from "@mui/material";
import { SkipToMain } from "@sage/sage-network-shared-ui/base-components";
import { Suspense } from "react";
import { I18nextProvider } from "react-i18next";
import { RouterProvider } from "react-router-dom";

import { router } from "@/globals/routes";
import ErrorBoundary from "@components/ErrorBoundary";
import FullScreenLoader from "@components/FullScreenLoader";
import { FlowSessionProvider } from "@providers/FlowSessionProvider";
import { PendoProvider } from "@providers/pendo";
import { QueryProvider } from "@providers/QueryProvider";
import { SnackbarProvider, GlobalSnackbar } from "@providers/SnackbarProvider";

import globalCssStyles from "./globalStyles";
import {
  i18nGlobal,
  I18nSetter,
  ActiveNamespaces,
  FallbackLanguage,
  MFEName
} from "./i18n";
import { theme } from "./theme";

const App: React.FC = () => {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <ErrorBoundary>
        <I18nextProvider i18n={i18nGlobal}>
          <I18nSetter
            LocalMFEName={MFEName}
            noSuspense
            activeNamespaces={ActiveNamespaces as Record<string, string[]>}
            fallbackLanguage={FallbackLanguage}
          >
            <QueryProvider>
              <ThemeProvider theme={theme}>
                <GlobalStyles styles={globalCssStyles} />
                <FlowSessionProvider
                  onSessionExpired={() =>
                    void router.navigate("/session-expired", { replace: true })
                  }
                >
                  <PendoProvider>
                    <SkipToMain />
                    <SnackbarProvider>
                      <RouterProvider router={router} />
                      <GlobalSnackbar />
                    </SnackbarProvider>
                  </PendoProvider>
                </FlowSessionProvider>
              </ThemeProvider>
            </QueryProvider>
          </I18nSetter>
        </I18nextProvider>
      </ErrorBoundary>
    </Suspense>
  );
};

export default App;
